import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { IonModal, ModalController } from '@ionic/angular';
import { ModalHelperService } from 'src/app/services/modal-helper';
import { StorageService } from 'src/app/services/storage.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';
import { CapacitorHttp } from '@capacitor/core';  
interface GooglePlace {
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}
interface GoogleGeocodeResponse {
  status: string;
  results: GooglePlace[];
  error_message?: string;
}

@Component({
  selector: 'app-lifetime-sws-air',
  templateUrl: './lifetime-sws-air.component.html',
  styleUrls: ['./lifetime-sws-air.component.scss'],
  standalone: false,
})
export class LifetimeSwsAirComponent implements OnInit {
  projectData: any = null;
  isFormValid = true;
  @ViewChild('placesModal') placesModal!: IonModal;

  locationQuery: string = '';
  latitude: number | null = null;
  longitude: number | null = null;
  isSearching: boolean = false;
  showPlacesModal: boolean = false;
  searchResults: GooglePlace[] = [];

  private readonly GOOGLE_API_KEY = environment.GOOGLE_API_KEY;
  private readonly MIN_LATITUDE = -90;
  private readonly MAX_LATITUDE = 90;
  private readonly MIN_LONGITUDE = -180;
  private readonly MAX_LONGITUDE = 180;

  constructor(private modalController: ModalController, private storageService: StorageService,
    private modalHelper: ModalHelperService, private http: HttpClient, private toastservice: ToastService) { }

  ngOnInit() {
    this.loadSavedLocation();
  }

  get displayLatitude(): string {
    return this.latitude !== null
      ? this.latitude.toLocaleString(undefined, { minimumFractionDigits: 7, maximumFractionDigits: 7 })
      : '-';
  }

  get displayLongitude(): string {
    return this.longitude !== null
      ? this.longitude.toLocaleString(undefined, { minimumFractionDigits: 7, maximumFractionDigits: 7 })
      : '-';
  }

  async loadSavedLocation() {
    try {
      const projectData = await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        if (project.lifetime) {
          this.locationQuery = project.lifetime.installationPlace || '';
          this.latitude = project.lifetime.latitude || null;
          this.longitude = project.lifetime.longitude || null;
        }
      }
    } catch (error) {
      console.error('Error loading saved location:', error);
    }
  }

  onSearchChange() {
    // Enable/disable search button based on input
    if (!this.locationQuery.trim()) {
      this.latitude = null;
      this.longitude = null;
    }
  }

  clearSearch() {
    this.locationQuery = '';
    this.latitude = null;
    this.longitude = null;
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }

  async next() {
    if (!this.locationQuery || this.latitude === null || this.longitude === null) {
      await this.toastservice.show('Please search for a location first');
      return;
    }
    const locationData = {
      installationPlace: this.locationQuery,
      latitude: this.latitude,
      longitude: this.longitude
    };
    console.log('Passing location data:', locationData);

    await this.modalController.dismiss(locationData, 'confirm');
  }

  async searchCoordinates() {
    if (!this.locationQuery.trim()) {
      await this.toastservice.show('Please enter a location');
      return;
    }

    this.isSearching = true;

    try {
      const response = await CapacitorHttp.get({
        url: 'https://maps.googleapis.com/maps/api/geocode/json',
        params: {
          key:      this.GOOGLE_API_KEY,
          address:  this.locationQuery.trim(),
          language: 'en',
        },
      });
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP ${response.status}`);
      }
            const data: GoogleGeocodeResponse = response.data;

      if (data?.status === 'OK') {
        this.handleSearchResults(data.results);
      } else if (data?.status === 'ZERO_RESULTS') {
        await this.toastservice.show('Location not found. Please try a different search.');
      } else {
        const errorMsg = data?.error_message || 'Failed to search location';
        await this.toastservice.show(errorMsg);
      }
    } catch (error: any) {
      console.error('Search error:', error);
      const errorMessage = error?.message?.includes('timeout')
        ? 'Request timeout. Please try again.'
        : error?.status === 0
          ? 'No internet connection'
          : error?.message || 'An error occurred while searching';

      await this.toastservice.show(errorMessage);
    } finally {
      this.isSearching = false;
    }
  }

  private handleSearchResults(results: GooglePlace[]) {
    if (!results || results.length === 0) {
      this.toastservice.show('Location not found');
      return;
    }

    // Filter valid results
    const validResults = results.filter(
      place => place.formatted_address &&
        place.geometry &&
        place.geometry.location
    );

    if (validResults.length === 0) {
      this.toastservice.show('Location not found');
      return;
    }

    if (validResults.length === 1) {
      // Single result - apply directly
      this.applyPlace(validResults[0]);
    } else {
      // Multiple results - show selection modal
      this.searchResults = validResults;
      this.showPlacesModal = true;
    }
  }

  selectPlace(place: GooglePlace) {
    this.applyPlace(place);
    this.closePlacesModal();
  }

  private applyPlace(place: GooglePlace) {
    this.locationQuery = place.formatted_address;
    this.latitude = place.geometry.location.lat;
    this.longitude = place.geometry.location.lng;

    // Validate coordinates
    if (this.isValidCoordinates()) {
      this.saveLocation();
    } else {
      this.toastservice.show('Invalid coordinates received');
    }
  }

  private isValidCoordinates(): boolean {
    return this.latitude !== null &&
      this.longitude !== null &&
      this.latitude >= this.MIN_LATITUDE &&
      this.latitude <= this.MAX_LATITUDE &&
      this.longitude >= this.MIN_LONGITUDE &&
      this.longitude <= this.MAX_LONGITUDE;
  }

  private async saveLocation() {
    try {
      const projectData = await this.storageService.getProject();
      const project = projectData ? JSON.parse(projectData) : {};

      if (!project.lifetime) {
        project.lifetime = {};
      }

      project.lifetime.installationPlace = this.locationQuery;
      project.lifetime.latitude = this.latitude;
      project.lifetime.longitude = this.longitude;
      project.lifetime.rating = null; // Reset rating when location changes

      await this.storageService.updateProject({
        lifetime: project.lifetime
      });

      console.log('Location saved:', {
        place: this.locationQuery,
        lat: this.latitude,
        lng: this.longitude
      });
    } catch (error) {
      console.error('Error saving location:', error);
    }
  }

  closePlacesModal() {
    this.showPlacesModal = false;
    this.searchResults = [];
  }

  onPlacesModalDismiss() {
    this.showPlacesModal = false;
    this.searchResults = [];
  }

  close() {
    this.modalController.dismiss();
  }
}

